import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/ui/Button";
import { sendSms } from "@/lib/sms";

function normalizeError(e, genericMsg) {
  const data = e?.response?.data;
  if (!data) return e?.message || genericMsg;
  if (typeof data === "string") return data;
  try {
    const parts = [];
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
      else if (typeof v === "string") parts.push(`${k}: ${v}`);
      else parts.push(`${k}: ${JSON.stringify(v)}`);
    }
    return parts.length ? parts.join(" • ") : genericMsg;
  } catch {
    return genericMsg;
  }
}

function toLocalDateTimeInputValue(d) {
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function SmsComposeModal({
  isOpen,
  onClose,
  appointmentId,
  clientId,
  defaultPhone = "",
  defaultBody = "",
}) {
  const { t } = useTranslation();
  const hasAppointment = Boolean(appointmentId);

  const modeOptions = useMemo(() => {
    const base = [{ value: "custom", label: t("sms.modeCustom") }];
    if (hasAppointment) {
      base.push(
        { value: "appointment_initial", label: t("sms.modeConfirmation") },
        { value: "appointment_reminder", label: t("sms.modeReminder") }
      );
    }
    return base;
  }, [hasAppointment, t]);

  const [mode, setMode] = useState("custom");
  const [toPhone, setToPhone] = useState("");
  const [body, setBody] = useState("");
  const [sendNow, setSendNow] = useState(true);
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [force, setForce] = useState(false);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // init
  useEffect(() => {
    if (!isOpen) return;
    setMode(hasAppointment ? "appointment_initial" : "custom");
    setToPhone(defaultPhone || "");
    setBody(defaultBody || "");
    setSendNow(true);
    setScheduledLocal("");
    setForce(false);
    setError("");
    setOkMsg("");
  }, [isOpen, defaultPhone, defaultBody, hasAppointment]);

  // ajustari UX: pentru appointment_reminder default e "nu acum" (backend calculeaza 24h)
  useEffect(() => {
    if (!isOpen) return;
    if (mode === "appointment_reminder") {
      setSendNow(false);
      setScheduledLocal("");
    } else {
      setSendNow(true);
      setScheduledLocal("");
    }
    setError("");
    setOkMsg("");
  }, [mode, isOpen]);

  if (!isOpen) return null;

  const selectedMode = modeOptions.find((m) => m.value === mode) || modeOptions[0];

  const showBody = mode === "custom";

  const scheduledAtIso = (() => {
    if (sendNow) return new Date().toISOString();
    if (mode === "appointment_reminder") return undefined; // backend calculeaza (24h inainte)
    if (!scheduledLocal) return undefined;
    const d = new Date(scheduledLocal);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString();
  })();

  const canSubmit = () => {
    if (!(toPhone || clientId || appointmentId)) return false;
    if (showBody && !body.trim()) return false;
    if (!sendNow && mode === "custom" && !scheduledLocal) return false;
    return true;
  };

  const handleSend = async () => {
    setSending(true);
    setError("");
    setOkMsg("");

    try {
      const data = await sendSms({
        mode,
        appointmentId,
        clientId,
        toPhone: toPhone?.trim() || undefined,
        body: showBody ? body?.trim() : (body?.trim() || undefined),
        scheduledAt: scheduledAtIso,
        force,
      });

      const when = data?.scheduled_at ? new Date(data.scheduled_at) : null;
      if (when && !Number.isNaN(when.getTime())) {
        setOkMsg(t("sms.queuedMsgScheduled", { when: when.toLocaleString("ro-RO") }));
      } else {
        setOkMsg(t("sms.queuedMsg"));
      }

      // inchidem dupa un scurt delay (UX)
      setTimeout(() => {
        onClose?.();
      }, 650);
    } catch (e) {
      setError(normalizeError(e, t("sms.genericError")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[220] flex justify-center items-end sm:items-center bg-black/40 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full bg-surface text-foreground shadow-xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 sm:px-6 py-3 sm:py-4">
          <div>
            <h2 className="text-lg font-semibold">{t("sms.send")}</h2>
            <div className="text-xs text-muted-foreground">{selectedMode?.label}</div>
          </div>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800"
            onClick={onClose}
            disabled={sending}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {error ? (
            <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {okMsg ? (
            <div className="mb-4 rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              {okMsg}
            </div>
          ) : null}

          {/* Mode */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t("sms.messageType")}</label>
            <select
              className="w-full rounded border border-border px-3 py-2 text-sm bg-input text-foreground"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={sending}
            >
              {modeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {mode === "appointment_reminder" ? (
              <div className="mt-1 text-xs text-muted-foreground">
                {t("sms.reminderNote")}
              </div>
            ) : null}
          </div>

          {/* Recipient */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t("sms.recipientLabel")}</label>
            <input
              type="tel"
              className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm"
              placeholder="07xx xxx xxx"
              value={toPhone}
              onChange={(e) => setToPhone(e.target.value)}
              disabled={sending}
            />
            <div className="mt-1 text-xs text-muted-foreground">
              {t("sms.recipientNote")}
            </div>
          </div>

          {/* When */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">{t("sms.when")}</label>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="sms_when"
                  checked={sendNow}
                  onChange={() => setSendNow(true)}
                  disabled={sending}
                />
                {t("sms.sendNow")}
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="sms_when"
                  checked={!sendNow}
                  onChange={() => setSendNow(false)}
                  disabled={sending || mode === "appointment_reminder"}
                />
                {t("sms.schedule")}
              </label>
            </div>

            {!sendNow && mode !== "appointment_reminder" ? (
              <div className="mt-2">
                <input
                  type="datetime-local"
                  className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm"
                  value={scheduledLocal}
                  onChange={(e) => setScheduledLocal(e.target.value)}
                  disabled={sending}
                  min={toLocalDateTimeInputValue(new Date())}
                />
              </div>
            ) : null}
          </div>

          {/* Body (custom) */}
          {showBody ? (
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t("sms.messageLabel")}</label>
              <textarea
                rows={5}
                className="w-full rounded border border-border bg-input text-foreground px-3 py-2 text-sm resize-none"
                placeholder={t("sms.messagePlaceholder")}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={sending}
              />
              <div className="mt-1 text-xs text-muted-foreground">{t("sms.messageNote")}</div>
            </div>
          ) : null}

          {/* Force */}
          <div className="mb-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                disabled={sending}
              />
              {t("sms.forceLabel")}
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <Button type="button" variant="outline" fullWidth onClick={onClose} disabled={sending}>
            {t("common.close")}
          </Button>
          <Button
            type="button"
            variant="default"
            fullWidth
            loading={sending}
            disabled={!canSubmit() || sending}
            onClick={handleSend}
          >
            {t("sms.sendBtn")}
          </Button>
        </div>
      </div>
    </div>
  );
}
